import type { RefObject, Dispatch, SetStateAction, PointerEventHandler } from 'react';
import { useEffect } from 'react';

import { MAX_ZOOM_SCALE, MIN_ZOOM_SCALE, TRACKPAD_WHEEL_IDLE_MS, TRACKPAD_WHEEL_ZOOM_SENSITIVITY } from '@/lib/constants/zoom.ts';
import type {
  ElementInteractionHandlers,
  FixedZoomAnchor,
  GesturePinchSession,
  PanState,
  Point,
  TouchPinchSession,
  WheelPinchSession
} from '@/lib/types/zoom.ts';
import { getCenterPoint, getDistance, getFixedZoomAnchor } from '@/lib/utils/zoom.ts';

export const usePointerZoomEvents = ({
  isAllowZoom,
  previewContainerRef,
  activePointersRef,
  pinchStartDistanceRef,
  pinchStartZoomScaleRef,
  pinchAnchorRef,
  panStateRef,
  zoomScaleRef,
  previewScaleRef,
  startPan,
  setContinuousZoomWithAnchor,
  applyPinchZoom,
  onPinchStateChange
}: {
  isAllowZoom: boolean;
  previewContainerRef: RefObject<HTMLDivElement | null>;
  activePointersRef: RefObject<Map<number, Point>>;
  pinchStartDistanceRef: RefObject<number | null>;
  pinchStartZoomScaleRef: RefObject<number>;
  pinchAnchorRef: RefObject<FixedZoomAnchor | null>;
  panStateRef: RefObject<PanState | null>;
  zoomScaleRef: RefObject<number>;
  previewScaleRef: RefObject<number>;
  startPan: (pointerId: number, point: Point) => void;
  setContinuousZoomWithAnchor: (nextZoomScale: number, anchor: { contentX: number; contentY: number; viewportX: number; viewportY: number } | null) => void;
  applyPinchZoom: (fixedZoomAnchor: FixedZoomAnchor, centerPoint: Point, nextZoomScale: number) => void;
  onPinchStateChange?: Dispatch<SetStateAction<number>>;
}): ElementInteractionHandlers => {
  const setPointerCaptureSafely = (element: HTMLDivElement, pointerId: number) => {
    if (element.hasPointerCapture(pointerId)) {
      return;
    }

    element.setPointerCapture(pointerId);
  };

  const releasePointerCaptureSafely = (element: HTMLDivElement, pointerId: number) => {
    if (!element.hasPointerCapture(pointerId)) {
      return;
    }

    element.releasePointerCapture(pointerId);
  };

  const captureActivePointers = (previewContainer: HTMLDivElement) => {
    activePointersRef.current.forEach((_, pointerId) => {
      setPointerCaptureSafely(previewContainer, pointerId);
    });
  };

  const releaseActivePointers = (previewContainer: HTMLDivElement) => {
    activePointersRef.current.forEach((_, pointerId) => {
      releasePointerCaptureSafely(previewContainer, pointerId);
    });
  };

  const resetPointerInteraction: PointerEventHandler<HTMLDivElement> = (event) => {
    const eventTarget = event.currentTarget as HTMLDivElement;
    releasePointerCaptureSafely(eventTarget, event.pointerId);
    activePointersRef.current.delete(event.pointerId);

    onPinchStateChange?.(0);
    if (panStateRef.current?.pointerId === event.pointerId) {
      panStateRef.current = null;
    }

    if (activePointersRef.current.size < 2) {
      pinchStartDistanceRef.current = null;
      pinchAnchorRef.current = null;

      if (zoomScaleRef.current <= MIN_ZOOM_SCALE) {
        releaseActivePointers(eventTarget);
      }
    }

    if (activePointersRef.current.size !== 1 || zoomScaleRef.current <= MIN_ZOOM_SCALE) {
      panStateRef.current = null;
      return;
    }

    const [remainingPointer] = Array.from(activePointersRef.current.entries());

    if (!remainingPointer) {
      return;
    }

    const [pointerId, point] = remainingPointer;
    setPointerCaptureSafely(eventTarget, pointerId);
    startPan(pointerId, point);
  };

  if (!isAllowZoom) {
    return {};
  }

  return {
    onPointerDown: (event) => {
      const point = { x: event.clientX, y: event.clientY };
      const isZoomedIn = zoomScaleRef.current > MIN_ZOOM_SCALE;

      activePointersRef.current.set(event.pointerId, point);

      if (activePointersRef.current.size === 1) {
        onPinchStateChange?.(1);
        if (!isZoomedIn) {
          panStateRef.current = null;
          return;
        }

        setPointerCaptureSafely(event.currentTarget, event.pointerId);
        startPan(event.pointerId, point);
        return;
      }

      if (activePointersRef.current.size !== 2) {
        return;
      }

      panStateRef.current = null;
      captureActivePointers(event.currentTarget);
      onPinchStateChange?.(2);

      const [firstPointer, secondPointer] = Array.from(activePointersRef.current.values());
      const previewContainer = previewContainerRef.current;

      if (!firstPointer || !secondPointer || !previewContainer) {
        pinchAnchorRef.current = null;
        return;
      }

      pinchStartDistanceRef.current = getDistance(firstPointer, secondPointer);
      pinchStartZoomScaleRef.current = zoomScaleRef.current;
      pinchAnchorRef.current = getFixedZoomAnchor(previewContainer, getCenterPoint(firstPointer, secondPointer), previewScaleRef.current);
    },
    onPointerMove: (event) => {
      if (!activePointersRef.current.has(event.pointerId)) {
        return;
      }

      const point = { x: event.clientX, y: event.clientY };
      activePointersRef.current.set(event.pointerId, point);

      if (activePointersRef.current.size === 2 && pinchStartDistanceRef.current !== null) {
        const [firstPointer, secondPointer] = Array.from(activePointersRef.current.values());
        const pinchAnchor = pinchAnchorRef.current;
        if (!firstPointer || !secondPointer) {
          return;
        }

        const nextZoomScale = Math.min(
          Math.max((pinchStartZoomScaleRef.current * getDistance(firstPointer, secondPointer)) / pinchStartDistanceRef.current, MIN_ZOOM_SCALE),
          MAX_ZOOM_SCALE
        );

        if (pinchAnchor) {
          applyPinchZoom(pinchAnchor, getCenterPoint(firstPointer, secondPointer), nextZoomScale);
          return;
        }

        setContinuousZoomWithAnchor(nextZoomScale, null);
        return;
      }

      const previewContainer = previewContainerRef.current;
      const panState = panStateRef.current;

      if (!previewContainer || !panState || panState.pointerId !== event.pointerId) {
        return;
      }

      previewContainer.scrollLeft = panState.startScrollLeft - (event.clientX - panState.startPointer.x);
      previewContainer.scrollTop = panState.startScrollTop - (event.clientY - panState.startPointer.y);
    },
    onPointerUp: resetPointerInteraction,
    onPointerCancel: resetPointerInteraction
  };
};

export const useWheelZoomEvents = ({
  isAllowZoom,
  previewContainerRef,
  zoomScaleRef,
  previewScaleRef,
  wheelPinchSessionRef,
  wheelPinchTimeoutRef,
  gesturePinchSessionRef,
  touchPinchSessionRef,
  applyPinchZoom
}: {
  isAllowZoom: boolean;
  previewContainerRef: RefObject<HTMLDivElement | null>;
  zoomScaleRef: RefObject<number>;
  previewScaleRef: RefObject<number>;
  wheelPinchSessionRef: RefObject<WheelPinchSession | null>;
  wheelPinchTimeoutRef: RefObject<number | null>;
  gesturePinchSessionRef: RefObject<GesturePinchSession | null>;
  touchPinchSessionRef: RefObject<TouchPinchSession | null>;
  applyPinchZoom: (fixedZoomAnchor: FixedZoomAnchor, point: Point, nextZoomScale: number) => void;
}) => {
  useEffect(() => {
    const previewContainer = previewContainerRef.current;

    if (!previewContainer || !isAllowZoom) {
      return;
    }

    const clearWheelPinchSession = () => {
      wheelPinchSessionRef.current = null;

      if (wheelPinchTimeoutRef.current === null) {
        return;
      }

      window.clearTimeout(wheelPinchTimeoutRef.current);
      wheelPinchTimeoutRef.current = null;
    };

    const scheduleWheelPinchSessionClear = () => {
      if (wheelPinchTimeoutRef.current !== null) {
        window.clearTimeout(wheelPinchTimeoutRef.current);
      }

      wheelPinchTimeoutRef.current = window.setTimeout(() => {
        wheelPinchSessionRef.current = null;
        wheelPinchTimeoutRef.current = null;
      }, TRACKPAD_WHEEL_IDLE_MS);
    };

    const handleTrackpadWheel = (event: WheelEvent) => {
      if (!event.ctrlKey) {
        return;
      }

      event.preventDefault();

      const point = {
        x: event.clientX,
        y: event.clientY
      };
      let wheelPinchSession = wheelPinchSessionRef.current;

      if (!wheelPinchSession) {
        wheelPinchSession = {
          accumulatedDeltaY: 0,
          startZoomScale: zoomScaleRef.current,
          anchor: getFixedZoomAnchor(previewContainer, point, previewScaleRef.current)
        };
        wheelPinchSessionRef.current = wheelPinchSession;
      }

      wheelPinchSession.accumulatedDeltaY += event.deltaY;
      applyPinchZoom(
        wheelPinchSession.anchor,
        point,
        wheelPinchSession.startZoomScale * Math.exp(-wheelPinchSession.accumulatedDeltaY * TRACKPAD_WHEEL_ZOOM_SENSITIVITY)
      );
      scheduleWheelPinchSessionClear();
    };

    previewContainer.addEventListener('wheel', handleTrackpadWheel, { passive: false });
    return () => {
      previewContainer.removeEventListener('wheel', handleTrackpadWheel);

      clearWheelPinchSession();
      gesturePinchSessionRef.current = null;
      touchPinchSessionRef.current = null;
    };
  }, [
    applyPinchZoom,
    gesturePinchSessionRef,
    isAllowZoom,
    previewContainerRef,
    previewScaleRef,
    touchPinchSessionRef,
    wheelPinchSessionRef,
    wheelPinchTimeoutRef,
    zoomScaleRef
  ]);
};
